import React, { useEffect, useState } from "react";
import Container from "../../components/Container";
import Breadcrumb from "../../components/Breadcrumb";
import renderDataTable from "../../helper/renderDataTable";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

/* ── helpers ── */
const getTodayDate = () => new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
const getCurrentMonth = () => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${d.getFullYear()}-${mm}`; // "YYYY-MM"
};

const Users = () => {
    const { id } = useParams();

    /* ================= REDUX TOKEN ================= */
    const token = useSelector((state) => state.user.userToken);

    /* ================= MONTH ================= */
    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth); // ← current month

    /* ================= TABLE DATA ================= */
    const [tableData, setTableData] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageLimit, setPageLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    const [loading, setLoading] = useState(false);

    /* ================= MODAL ================= */
    const [openModal, setOpenModal] = useState(false);
    const [activeRowIndex, setActiveRowIndex] = useState(null);

    /* ================= INVOICE MODAL ================= */
    const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
    const [activeInvoiceRow, setActiveInvoiceRow] = useState(null);

    /* ================= API PREFILL DATA ================= */
    const [apiPrefillData, setApiPrefillData] = useState({
        company_name: "",
        company_address: "",
        gst_no: "",
        reference_id: "",
    });

    /* ================= INVOICE FORM ================= */
    const defaultInvoiceForm = {
        company_name: "",
        company_address: "",
        gst_no: "",
        reference_id: "",
        payment_date: getTodayDate(), // ← today's date as default
    };
    const [invoiceForm, setInvoiceForm] = useState(defaultInvoiceForm);

    /* ================= LOAD DATA ================= */
    useEffect(() => {
        if (!id || !token) return;

        const fetchReconData = async () => {
            try {
                setLoading(true);

                const response = await fetch(
                    "https://api.myad360.com/api/v1/FNV89uJ9sud/admin/get_reconcilations_data.php",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json",
                            token: token,
                        },
                        body: JSON.stringify({
                            user_id: id,
                            month: selectedMonth,
                        }),
                    }
                );

                const result = await response.json();
                console.log("FETCH API RESPONSE:", result);

                if (response.ok && result?.status === "1" && result?.data) {
                    const row = Array.isArray(result.data)
                        ? result.data[0]
                        : result.data;

                    if (row) {
                        const payout = Number(row.payout) || 0;
                        const deductions = Number(row.deductions) || 0;

                        const finalRow = {
                            id: row.id || Date.now(),
                            month: row.month || selectedMonth,
                            payout,
                            deductions,
                            netPayment: payout - deductions,
                            payment_status: Number(row.payment_status) || 0,
                            is_invoice_created: Number(row.is_invoice_created) || 0, // ← NEW
                        };

                        console.log("SET TABLE ROW:", finalRow);
                        setTableData([finalRow]);

                        /* ── Store prefill data from API response ── */
                        setApiPrefillData({
                            company_name: row.company_name || "",
                            company_address: row.company_address || "",
                            gst_no: row.gst_no || "",
                            reference_id: row.reference_id || "",
                        });

                        return;
                    }
                }

                setEmptyRow();
            } catch (error) {
                console.error("FETCH ERROR:", error);
                setEmptyRow();
            } finally {
                setLoading(false);
            }
        };

        const setEmptyRow = () => {
            const empty = {
                id: Date.now(),
                month: selectedMonth,
                payout: 0,
                deductions: 0,
                netPayment: 0,
                payment_status: 0,
                is_invoice_created: 0, // ← NEW
            };
            console.log("EMPTY ROW SET:", empty);
            setTableData([empty]);
            setApiPrefillData({
                company_name: "",
                company_address: "",
                gst_no: "",
                reference_id: "",
            });
        };

        fetchReconData();
    }, [selectedMonth, id, token]);

    /* ================= HANDLE DEDUCTION ================= */
    const handleChange = (index, value) => {
        const updated = [...tableData];
        const payout = Number(updated[index].payout) || 0;
        const deductions = Number(value) || 0;

        updated[index] = {
            ...updated[index],
            deductions,
            netPayment: payout - deductions,
        };

        console.log("DEDUCTION CHANGE:", updated[index]);
        setTableData(updated);
    };

    /* ================= SAVE RECON ================= */
    const handleSubmit = async () => {
        try {
            setLoading(true);

            const row = tableData[0];
            console.log("SAVE PAYLOAD:", row);

            const response = await fetch(
                "https://api.myad360.com/api/v1/FNV89uJ9sud/admin/add_reconcilations.php",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        token: token,
                    },
                    body: JSON.stringify({
                        user_id: id,
                        month: selectedMonth,
                        payout: row.payout,
                        deductions: row.deductions,
                        net_payment: row.netPayment,
                    }),
                }
            );

            const result = await response.json();
            console.log("SAVE RESPONSE:", result);

            if (result?.status !== "1") {
                throw new Error(result?.msg || "Save failed");
            }

            alert("Saved successfully");
        } catch (error) {
            console.error("SAVE ERROR:", error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    /* ================= PAYMENT STATUS CHANGE ================= */
    const handlePaymentDone = async () => {
        try {
            setLoading(true);

            const row = tableData[activeRowIndex];
            console.log("PAYMENT CLICKED:", row);

            const response = await fetch(
                "https://api.myad360.com/api/v1/FNV89uJ9sud/admin/reconcilations_payment_status_change.php",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        token: token,
                    },
                    body: JSON.stringify({
                        user_id: id,
                        month: row.month,
                    }),
                }
            );

            const result = await response.json();
            console.log("PAYMENT API RESPONSE:", result);

            if (result?.status !== "1") {
                throw new Error(result?.msg || "Payment failed");
            }

            const updated = [...tableData];
            updated[activeRowIndex] = {
                ...updated[activeRowIndex],
                payment_status: 1,
            };

            console.log("UPDATED TABLE:", updated);
            setTableData(updated);
            setOpenModal(false);

            alert("Payment Success");
        } catch (error) {
            console.error("PAYMENT ERROR:", error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    /* ================= INVOICE FORM CHANGE ================= */
    const handleInvoiceFormChange = (e) => {
        const { name, value } = e.target;
        setInvoiceForm((prev) => ({ ...prev, [name]: value }));
    };

    /* ================= SEND INVOICE ================= */
    const handleSendInvoice = async () => {
        const { company_name, company_address, gst_no, reference_id, payment_date } = invoiceForm;
        if (!company_name || !company_address || !gst_no || !reference_id || !payment_date) {
            alert("Please fill in all fields before submitting.");
            return;
        }

        try {
            setLoading(true);

            const payload = {
                user_id: id,
                month: activeInvoiceRow?.month,
                payout: activeInvoiceRow?.payout,
                deductions: activeInvoiceRow?.deductions,
                net_payment: activeInvoiceRow?.netPayment,
                company_name,
                company_address,
                gst_no,
                reference_id,
                payment_date,
            };

            console.log("SEND INVOICE PAYLOAD:", payload);

            const response = await fetch(
                "https://api.myad360.com/send_invoice.php",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        token: token,
                    },
                    body: JSON.stringify(payload),
                }
            );

            const result = await response.json();
            console.log("SEND INVOICE RESPONSE:", result);

            if (result?.status !== "1") {
                throw new Error(result?.msg || "Send invoice failed");
            }

            alert("Invoice sent successfully");
            setOpenInvoiceModal(false);
            setActiveInvoiceRow(null);
            setInvoiceForm(defaultInvoiceForm);
        } catch (error) {
            console.error("SEND INVOICE ERROR:", error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    /* ================= STATUS UI ================= */
    const renderStatus = (payment_status) => {
        console.log("RENDER STATUS:", payment_status);
        return Number(payment_status) === 1 ? (
            <span className="text-orange-600 font-semibold">Success</span>
        ) : (
            <span className="text-yellow-600 font-semibold">Pending</span>
        );
    };

    /* ================= TABLE COLUMNS ================= */
    const column = [
        { name: "Month", selector: (row) => row.month },

        {
            name: "Payout",
            cell: (row) => (
                <input
                    readOnly
                    value={row.payout.toFixed(2)}
                    className="border px-2 py-1 rounded w-full bg-gray-100"
                />
            ),
        },

        {
            name: "Deductions",
            cell: (row, index) => (
                <input
                    type="number"
                    value={row.deductions}
                    onChange={(e) => handleChange(index, e.target.value)}
                    className="border px-2 py-1 rounded w-full"
                />
            ),
        },

        {
            name: "Net Payment",
            cell: (row) => (
                <input
                    readOnly
                    value={row.netPayment.toFixed(2)}
                    className="border px-2 py-1 rounded w-full bg-gray-100"
                />
            ),
        },

        {
            name: "Status",
            cell: (row) => renderStatus(row.payment_status),
        },

        {
            name: "Action",
            cell: (row, index) => (
                <div className="flex items-center gap-2">
                    {/* ── Confirm Payment icon ── */}
                    <div
                        onClick={() => {
                            if (row.payment_status === 1) return;
                            setActiveRowIndex(index);
                            setOpenModal(true);
                        }}
                        className={`w-9 h-9 flex items-center justify-center rounded-full
                            ${row.payment_status === 1
                                ? "bg-gray-200 cursor-not-allowed"
                                : "bg-orange-50 hover:bg-orange-100 cursor-pointer"
                            }`}
                    >
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={
                                row.payment_status === 1
                                    ? "text-gray-400"
                                    : "text-orange-600"
                            }
                        >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M9 12l2 2 4-4" />
                        </svg>
                    </div>

                    {/* ── Invoice icon — show only when is_invoice_created === 0 ── */}
                    {row.is_invoice_created === 0 && (
                        <div
                            onClick={() => {
                                setActiveInvoiceRow(row);
                                setInvoiceForm({
                                    company_name: apiPrefillData.company_name,
                                    company_address: apiPrefillData.company_address,
                                    gst_no: apiPrefillData.gst_no,
                                    reference_id: "",
                                    payment_date: getTodayDate(), // ← always today when modal opens
                                });
                                setOpenInvoiceModal(true);
                            }}
                            title="Send Invoice"
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-orange-50 hover:bg-orange-100 cursor-pointer"
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-orange-500"
                            >
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="8" y1="13" x2="16" y2="13" />
                                <line x1="8" y1="17" x2="16" y2="17" />
                                <line x1="8" y1="9" x2="10" y2="9" />
                            </svg>
                        </div>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <Breadcrumb pageName="Payout" />

            <Container>
                <div className="flex items-end gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Select Month
                        </label>
                        <input
                            type="month"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className="border px-3 py-2 rounded"
                        />
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className={`px-6 py-2 rounded text-white ${loading
                            ? "bg-gray-400"
                            : "bg-orange-600 hover:bg-orange-700"
                            }`}
                    >
                        {loading ? "Saving..." : "Submit"}
                    </button>
                </div>

                <div className="border w-full p-3">
                    {renderDataTable(
                        tableData,
                        column,
                        false,
                        currentPage,
                        setPageLimit,
                        setCurrentPage,
                        totalPages,
                        pageLimit
                    )}
                </div>
            </Container>

            {/* ================= CONFIRM PAYMENT MODAL ================= */}
            {openModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md text-center">
                        <h2 className="text-xl font-semibold mb-4">
                            Confirm Payment
                        </h2>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to mark payment as completed?
                        </p>

                        <div className="flex justify-center gap-4">
                            <button
                                onClick={handlePaymentDone}
                                disabled={loading}
                                className={`px-5 py-2 rounded text-white ${loading
                                    ? "bg-gray-400"
                                    : "bg-orange-600 hover:bg-orange-700"
                                    }`}
                            >
                                {loading ? "Processing..." : "Done"}
                            </button>

                            <button
                                onClick={() => setOpenModal(false)}
                                className="px-5 py-2 bg-gray-300 rounded hover:bg-gray-400"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ================= SEND INVOICE MODAL ================= */}
            {openInvoiceModal && activeInvoiceRow && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg">

                        {/* Header */}
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-xl font-semibold text-gray-800">
                                Send Invoice
                            </h2>
                            <button
                                onClick={() => {
                                    setOpenInvoiceModal(false);
                                    setActiveInvoiceRow(null);
                                    setInvoiceForm(defaultInvoiceForm);
                                }}
                                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                            >
                                &times;
                            </button>
                        </div>

                        {/* Form fields */}
                        <div className="grid grid-cols-1 gap-4">

                            {/* Company Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Company Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="company_name"
                                    value={invoiceForm.company_name}
                                    onChange={handleInvoiceFormChange}
                                    placeholder="Enter company name"
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                                />
                            </div>

                            {/* Company Address */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Company Address <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    name="company_address"
                                    value={invoiceForm.company_address}
                                    onChange={handleInvoiceFormChange}
                                    placeholder="Enter company address"
                                    rows={2}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                                />
                            </div>

                            {/* GST No & Reference ID side by side */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        GST No <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="gst_no"
                                        value={invoiceForm.gst_no}
                                        onChange={handleInvoiceFormChange}
                                        placeholder="e.g. 22AAAAA0000A1Z5"
                                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Reference ID <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="reference_id"
                                        value={invoiceForm.reference_id}
                                        onChange={handleInvoiceFormChange}
                                        placeholder="e.g. REF-001"
                                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                                    />
                                </div>
                            </div>

                            {/* Payment Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Payment Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="payment_date"
                                    value={invoiceForm.payment_date}
                                    onChange={handleInvoiceFormChange}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setOpenInvoiceModal(false);
                                    setActiveInvoiceRow(null);
                                    setInvoiceForm(defaultInvoiceForm);
                                }}
                                className="px-5 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm font-medium"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleSendInvoice}
                                disabled={loading}
                                className={`px-5 py-2 rounded text-white text-sm font-medium ${loading
                                    ? "bg-gray-400"
                                    : "bg-blue-500 hover:bg-blue-600"
                                    }`}
                            >
                                {loading ? "Sending..." : "Send Invoice"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Users;